import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { ClosePreview, LocaleCode } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    JournalEntryLines,
    useAccountLabelMap,
} from '@/pages/accounting/books/journal/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type EntryPreviewDialogProps = {
    isOpen: boolean;
    /** Distinguishes the two year-end steps in copy and test ids. */
    kind: 'CLOSE_YEAR' | 'ROLLOVER';
    /** The entry as it will be posted; `null` while the dry run is in flight. */
    preview: ClosePreview | null;
    locale: LocaleCode;
    isLoading: boolean;
    /** Dry-run failure — nothing was written. */
    previewError: string | null;
    /** Commit failure. */
    commitError: string | null;
    isPending: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

/**
 * Preview-before-commit for the two consequential year-end actions.
 *
 * The entry is rendered with the journal drawer's own `JournalEntryLines`, so
 * what the user approves here is pixel-for-pixel what they will find in the
 * audit trail afterwards. The confirm button is never the first click: the
 * dialog has to be opened, and it only enables once a dry run has returned an
 * entry.
 *
 * The figures come from the backend verbatim. The screen does not recompute the
 * result or reconcile it against the P&L — that asymmetry (the P&L excludes
 * closing entries, equity's result includes them) is deliberate and lives
 * server-side.
 */
export const EntryPreviewDialog: React.FC<EntryPreviewDialogProps> = ({
    isOpen,
    kind,
    preview,
    locale,
    isLoading,
    previewError,
    commitError,
    isPending,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation('pages');
    const accountMap = useAccountLabelMap(locale);
    const isClose = kind === 'CLOSE_YEAR';
    const slot = isClose ? 'close-year' : 'rollover';
    const target = preview
        ? accountMap.get(preview.targetAccountId)
        : undefined;

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onCancel()}>
            <Modal.Backdrop>
                <Modal.Container className="max-sm:p-0" size="lg">
                    <Modal.Dialog
                        className="w-full max-w-2xl max-sm:m-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:rounded-none"
                        {...getNodeTestIdProp(`periods-preview-dialog-${slot}`)}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {isClose
                                    ? t('periods.closeYear.title')
                                    : t('periods.rollover.title')}
                            </Modal.Heading>
                            <p className="text-sm text-muted">
                                {isClose
                                    ? t('periods.closeYear.subtitle')
                                    : t('periods.rollover.subtitle')}
                            </p>
                        </Modal.Header>

                        <Modal.Body className="flex flex-col gap-4 pb-2">
                            {isLoading ? (
                                <div className="flex flex-col items-center gap-2 py-10 text-muted">
                                    <Icon
                                        aria-hidden
                                        className="size-7 animate-spin text-default-500"
                                        icon="gravity-ui:arrows-rotate-right"
                                    />
                                    <span className="text-sm">
                                        {t('periods.preview.loading')}
                                    </span>
                                </div>
                            ) : previewError ? (
                                <p
                                    className="rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        `periods-preview-error-${slot}`,
                                    )}
                                >
                                    {previewError}
                                </p>
                            ) : preview ? (
                                <>
                                    <p className="text-sm text-muted">
                                        {preview.entry.description}
                                    </p>
                                    <JournalEntryLines
                                        accountMap={accountMap}
                                        lines={preview.entry.lines}
                                        slot={slot}
                                        totalCreditTetri={
                                            preview.entry.validationState
                                                .totalCreditTetri
                                        }
                                        totalDebitTetri={
                                            preview.entry.validationState
                                                .totalDebitTetri
                                        }
                                    />
                                    <p
                                        className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg bg-surface-secondary px-4 py-3 text-sm"
                                        {...getNodeTestIdProp(
                                            `periods-preview-result-${slot}`,
                                        )}
                                    >
                                        <span className="text-muted">
                                            {isClose
                                                ? t('periods.preview.resultTo')
                                                : t(
                                                      'periods.preview.rolledInto',
                                                  )}
                                        </span>
                                        <span className="font-semibold tabular-nums text-foreground">
                                            {formatGel(preview.resultTetri)}
                                        </span>
                                        <span className="text-muted">
                                            {target
                                                ? `· ${target.code} ${target.name}`
                                                : `· ${preview.targetAccountId}`}
                                        </span>
                                    </p>
                                    <p className="flex items-start gap-2 text-xs text-muted">
                                        <Icon
                                            aria-hidden
                                            className="mt-0.5 shrink-0"
                                            icon="gravity-ui:circle-info"
                                        />
                                        {isClose
                                            ? t('periods.closeYear.note')
                                            : t('periods.rollover.note')}
                                    </p>
                                </>
                            ) : null}

                            {commitError ? (
                                <p
                                    className="text-sm text-danger"
                                    {...getNodeTestIdProp(
                                        `periods-commit-error-${slot}`,
                                    )}
                                >
                                    {commitError}
                                </p>
                            ) : null}
                        </Modal.Body>

                        <Modal.Footer>
                            <Button
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `periods-preview-cancel-${slot}`,
                                )}
                            >
                                {t('periods.actions.cancel')}
                            </Button>
                            <Button
                                // Never commit without a dry run in hand.
                                isDisabled={preview === null}
                                isPending={isPending}
                                onPress={onConfirm}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    `periods-preview-confirm-${slot}`,
                                )}
                            >
                                {isClose
                                    ? t('periods.actions.postClosingEntry')
                                    : t('periods.actions.postRollover')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
